/**
 * FamilyHistoryModal — фамилната анамнеза се въвежда тук (общо за всички модули).
 * Данни: GET/POST/DELETE /api/family-history
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, X, Plus, Trash2, Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

export default function FamilyHistoryModal({ open, onClose, onSaved }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [relation, setRelation] = useState("mother");
  const [picked, setPicked] = useState([]);
  const [age, setAge] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: res } = await axios.get(`${API}/family-history`);
      setData(res);
    } catch { setData(null); }
  }, [API]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const toggle = (key) =>
    setPicked((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));

  const save = async () => {
    if (!picked.length) { toast.error("Изберете поне едно заболяване"); return; }
    setSaving(true);
    try {
      const { data: res } = await axios.post(`${API}/family-history`, {
        relation,
        condition_ids: picked,
        age_at_diagnosis: age ? Number(age) : null,
      });
      setData((d) => ({ ...d, entries: res.entries, count: res.count, risk: res.risk }));
      setPicked([]);
      setAge("");
      toast.success(res.added ? `Добавени ${res.added} записа` : "Записът вече съществува");
      onSaved?.();
    } catch {
      toast.error("Записването не успя");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      const { data: res } = await axios.delete(`${API}/family-history/${id}`);
      setData((d) => ({ ...d, entries: res.entries, count: res.count, risk: res.risk }));
      onSaved?.();
    } catch { toast.error("Изтриването не успя"); }
  };

  const groups = (data?.conditions_meta || []).reduce((acc, c) => {
    (acc[c.group_bg] = acc[c.group_bg] || []).push(c);
    return acc;
  }, {});
  const panel = isDark ? "bg-slate-900 border-slate-700 text-slate-100"
                       : "bg-white border-slate-200 text-slate-900";
  const field = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300";

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center
                    bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      data-testid="family-history-modal">
      <div className={`w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl
                       border shadow-2xl ${panel}`}>
        <div className={`sticky top-0 flex items-start justify-between gap-3 p-4 border-b ${
          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-5 h-5 text-pink-500 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-extrabold">Фамилна анамнеза</h2>
              <p className="text-[11px] opacity-70">{data?.sync_note_bg}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10"
            data-testid="family-history-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* наследствен риск — веднага след записите */}
          {!!data?.risk && (
            <div className={`p-3 rounded-xl border-l-[3px] ${
              data.risk.high_count ? "border-red-500 bg-red-500/10"
              : data.risk.elevated_count ? "border-amber-500 bg-amber-500/10"
              : "border-emerald-500 bg-emerald-500/10"}`}
              data-testid="family-history-risk">
              <div className="text-[11px] font-bold inline-flex items-center gap-1">
                <ShieldAlert className="w-3.5 h-3.5" />
                Какво означава това за Вас
              </div>
              <div className="text-[12px] mt-1 font-semibold" data-testid="family-history-risk-headline">
                {data.risk.headline_bg}
              </div>
              {!!data.risk.items?.length && (
                <div className="mt-2 space-y-1.5">
                  {data.risk.items.map((r) => (
                    <div key={r.condition_id} className="text-[11px]"
                      data-testid={`family-history-risk-${r.condition_id}`}>
                      <span className={`font-bold ${
                        r.level === "high" ? "text-red-500"
                        : r.level === "elevated" ? "text-amber-600 dark:text-amber-400"
                        : "opacity-70"}`}>
                        {r.summary_bg}
                      </span>
                      <span className="block opacity-80">→ {r.advice_bg}</span>
                      {!!r.modules_bg?.length && (
                        <span className="block text-[10px] opacity-60">
                          Влиза в: {r.modules_bg.join(", ")}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="text-[9px] opacity-60 mt-1.5">{data.risk.disclaimer_bg}</div>
            </div>
          )}

          <div>
            <label className="text-[11px] font-bold opacity-70">Роднина</label>
            <select value={relation} onChange={(e) => setRelation(e.target.value)}
              className={`mt-1 w-full px-3 py-2 rounded-xl text-sm border ${field}`}
              data-testid="family-history-relation">
              {(data?.relatives_meta || []).map((r) => (
                <option key={r.key} value={r.key}>{r.label_bg}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[11px] font-bold opacity-70">
              Заболявания на този роднина (може повече от едно)
            </label>
            <div className="mt-1 space-y-2">
              {Object.entries(groups).map(([g, list]) => (
                <div key={g}>
                  <div className="text-[10px] font-bold uppercase opacity-50">{g}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {list.filter((c) => c.key !== "free_text").map((c) => (
                      <button key={c.key} onClick={() => toggle(c.key)}
                        className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
                          picked.includes(c.key)
                            ? "bg-pink-500 text-white border-pink-500"
                            : isDark ? "border-white/15 hover:bg-white/5" : "border-slate-300 hover:bg-slate-50"
                        }`}
                        data-testid={`family-history-cond-${c.key}`}>
                        {c.label_bg}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="text-[11px] font-bold opacity-70">Възраст при диагнозата</label>
              <input type="number" min="0" max="120" value={age}
                onChange={(e) => setAge(e.target.value)} placeholder="напр. 52"
                className={`mt-1 w-28 px-3 py-2 rounded-xl text-sm border ${field}`}
                data-testid="family-history-age" />
            </div>
            <button onClick={save} disabled={saving || !picked.length}
              className="px-4 py-2 rounded-xl text-sm font-bold text-white
                         bg-gradient-to-r from-pink-500 to-rose-600 hover:opacity-95
                         disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="family-history-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Добави
            </button>
          </div>

          <div>
            <div className="text-[10px] font-bold uppercase opacity-50 mb-1">
              Записани роднини ({data?.count || 0})
            </div>
            {!data?.entries?.length ? (
              <div className="text-xs opacity-60" data-testid="family-history-empty">
                Още няма записи. Започнете с майка, баща, сестра или брат — те тежат най-много.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="family-history-list">
                {data.entries.map((e) => (
                  <div key={e.id}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border ${
                      isDark ? "border-white/10" : "border-slate-200"}`}
                    data-testid={`family-history-entry-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold">
                        {e.relation_bg} · {e.condition_bg}
                      </div>
                      <div className="text-[10px] opacity-60">
                        {e.age_at_diagnosis ? `диагноза на ${e.age_at_diagnosis} г.` : "без възраст"}
                        {e.notes ? ` · ${e.notes}` : ""}
                      </div>
                    </div>
                    <button onClick={() => remove(e.id)}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500"
                      data-testid={`family-history-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="text-[9px] opacity-50">{data?.disclaimer_bg}</div>
        </div>
      </div>
    </div>
  );
}
